import { IMPORT_STATUS, errorCountOf } from 'components/import-total-cell/importTotals';
import { ROLE } from 'static';
import { userFullNameBuilder } from 'utils/userFullNameBuilder';

const { I_SUPER_ADMIN } = ROLE;

export interface NotificationFeedSender {
  userId?: string;
  fname?: string;
  lname?: string;
  preferredFname?: string;
  preferredLname?: string;
  profilePic?: string;
}

export interface NotificationFeedEvent {
  eventId?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  thumbnail?: string;
}

/** The `importData` sub-document a finished CSV import writes. */
export interface NotificationFeedImport {
  importDataId?: string;
  fileName?: string;
  totalRows?: number;
  totalImported?: number;
  /** The two halves of totalImported, which merges them. */
  totalCreated?: number;
  totalUpdated?: number;
  /** Absent when nothing was rejected. Signed by the backend on read. */
  reportFileLocationUrl?: string;
  /** 'Mentee' or 'Mentor'. Decides which participants list the row opens. */
  targetUserRole?: string;
}

export interface NotificationFeedDocument {
  _id?: string;
  content?: string;
  category?: string;
  subcategory?: string;
  isNotificationRead?: boolean;
  createdAt?: string;
  sender?: NotificationFeedSender;
  event?: NotificationFeedEvent;
  importData?: NotificationFeedImport;
}

/**
 * What an import row renders below its headline: the counts line, and the
 * error-report affordance. `errorCount: 0` means a clean import, which has no
 * report to offer.
 */
export interface NotificationImportReport {
  /** The second line, e.g. "138 users updated, 42 new users created". */
  summary: string;
  errorCount: number;
  reportUrl?: string;
  /**
   * Which badge the upload icon wears. Decided here, from the counts, rather
   * than in the component: the alternative is reading it back out of
   * `summary`, and a rendering detail should not depend on how a sentence was
   * worded.
   */
  state: 'ok' | 'partial' | 'failed';
}

export interface NotificationFeedRow {
  id: string;
  subcategory: string;
  title: string;
  body: string;
  avatarUrl?: string;
  avatarFirstName?: string;
  avatarLastName?: string;
  createdAt?: string;
  isRead: boolean;
  /**
   * Optional since imports arrived. An import row routes on `targetUserRole`,
   * to that role's participants list -- see IMPORT_ROUTES. It is absent only
   * when the notification carries no role, or one neither list covers, which a
   * row written before the role was stored can still do.
   */
  route?: string;
  routeState?: { tab: string };
  event: NotificationFeedEvent | null;
  importReport: NotificationImportReport | null;
}

interface FormatOptions {
  role?: string;
}

/**
 * Subcategories the CMS dropdown renders, and where clicking one goes.
 *
 * Short by design. The feed excludes the whole `Message` category on the
 * backend, which takes `MessageReaction` with it, and the remaining
 * subcategories (`Matched`, `Reminder`, `ContentAssigned`, the `Prompt`
 * category) only ever reach mentors and mentees. What is left for an admin is
 * the event approval request and the two RSVP responses. `CourseCompleted` has
 * no v2 equivalent at all, so the legacy row for it simply stops appearing.
 *
 * Anything unrecognised is skipped rather than guessed at, so a future backend
 * subcategory cannot render as a dead row with no destination.
 */
const ROUTES: Record<string, string> = {
  EventApproval: '/events',
  InvitationApproved: '/events',
  InvitationDeclined: '/events',
};

const IMPORT_SUBCATEGORY = 'ImportCompleted';

/**
 * Where an import row goes, keyed by the role it filled rather than by its
 * subcategory the way ROUTES is -- every import shares one subcategory and
 * they do not share a destination.
 *
 * The participants page picks its section from the path
 * (`views/participants/index.tsx`), so these are the whole navigation. Import
 * History itself is not reachable: it is a modal opened from a store flag,
 * with no URL of its own, so the list the import filled is the closest thing
 * to "show me what this did".
 *
 * A role with no entry -- or a notification written before the role was
 * carried -- leaves the row without a destination. It still renders and can
 * still be marked read; it just does not navigate.
 */
const IMPORT_ROUTES: Record<string, string> = {
  Mentee: '/participants/mentees',
  Mentor: '/participants/mentors',
};

/** "1 user" / "2 users", so the line does not read "1 users created". */
const userCount = (count: number): string => `${count} ${count === 1 ? 'user' : 'users'}`;

/**
 * The second line under an import headline: what the import did to the data.
 *
 * Two shapes, because a clean import and a partial one answer different
 * questions. With nothing rejected the denominator is noise -- every row
 * landed -- and what is worth saying is how the work split between existing
 * users and new ones. With rejections the split matters less than how far the
 * import got, so it reads as a ratio.
 *
 * The all-clean line drops either half when it is zero rather than printing
 * "0 users updated", which reads as a failure in a sentence about success.
 */
const buildSplit = (created: number, updated: number): string => {
  const parts: string[] = [];

  if (updated > 0) {
    parts.push(`${userCount(updated)} updated`);
  }

  if (created > 0) {
    parts.push(`${created} new ${created === 1 ? 'user' : 'users'} created`);
  }

  return parts.join(', ');
};

const buildImportSummary = (totalRows: number, totalImported: number, created: number, updated: number): string => {
  const split = buildSplit(created, updated);

  if (totalImported < totalRows) {
    // "imported", never "created". totalImported merges both halves, so calling
    // it created claimed every landed row was a new account -- an import of 160
    // where 138 landed, 100 of them updates, read as "138 users created". The
    // admin uses this line to decide whether to re-import, and "138 new users"
    // and "38 new users, 100 updated" call for different actions.
    const ratio = `${totalImported}/${totalRows} ${totalRows === 1 ? 'user' : 'users'} imported`;

    // The split is appended rather than replacing the ratio: a partial import
    // is first a question of how far it got, and the breakdown is the detail
    // under it. Same phrasing as the clean line, so the two read alike.
    return split ? `${ratio} — ${split}` : ratio;
  }

  // Nothing to break down: an empty file, or an import from before the split
  // was recorded. Falling back to the total keeps the line truthful either way.
  return split || `${userCount(totalImported)} imported`;
};

/**
 * The parts of an import row that `content` does not already carry.
 *
 * The headline is `content` verbatim -- the backend composes and stores the
 * whole sentence ("spring_roster.csv uploaded with 22 errors"), and the design
 * bolds exactly that. Recomposing it here from the counts would duplicate the
 * backend's templates and let the two drift; the counts are read only for the
 * second line and the error chip, which `content` has no way to express.
 *
 * Returns null when the payload cannot be trusted, which drops the row. That
 * matches how this file treats everything it does not recognise, and the
 * fields are required on the backend schema, so an absent one means something
 * is wrong rather than something being optional.
 */
const buildImportRow = (importData: NotificationFeedImport | undefined): NotificationImportReport | null => {
  const { totalRows, totalImported } = importData ?? {};

  if (typeof totalRows !== 'number' || typeof totalImported !== 'number') {
    return null;
  }

  // COMPLETED is a literal rather than a field because the payload carries no
  // status, and it does not need to: the backend writes an import notification
  // only after the parent processor has already recorded success. A job that
  // fails goes to recordFailure, which marks the import Failed and emits
  // nothing, and `ImportCompleted` is the only import subcategory that exists
  // -- there is no ImportFailed. An empty file cannot reach here either, since
  // validation rejects it before the import record is created, so totalRows is
  // always at least 1.
  //
  // So the shortfall is always meaningful: every row that did not land was
  // rejected, not merely unprocessed. If a failed job is ever given a
  // notification of its own, this stops being true and the payload needs a
  // status for this line to read.
  //
  // Shared with the Import History cell so the bell and the table can never
  // disagree about what counts as an error.
  const errorCount = errorCountOf(IMPORT_STATUS.COMPLETED, totalRows, totalImported);

  // Same order the backend's three templates use, and for the same reason: a
  // file where every row landed also satisfies "nothing was rejected", and one
  // where nothing landed also satisfies "some were rejected". Asking about
  // zero errors first and zero imports second is what separates them, and is
  // why an empty file badges as a success rather than a failure.
  let state: NotificationImportReport['state'] = 'partial';

  if (errorCount === 0) {
    state = 'ok';
  } else if (totalImported === 0) {
    state = 'failed';
  }

  return {
    summary: buildImportSummary(totalRows, totalImported, importData?.totalCreated ?? 0, importData?.totalUpdated ?? 0),
    errorCount,
    reportUrl: importData?.reportFileLocationUrl,
    state,
  };
};

/**
 * The events page tab to open, matching what the socket implementation did.
 * An approval request is the creator's own event for a local admin but someone
 * else's for a super admin, so only that row depends on the viewer's role.
 */
const resolveRouteState = (subcategory: string, role?: string): { tab: string } | undefined => {
  if (subcategory === 'EventApproval') {
    return { tab: role === I_SUPER_ADMIN ? 'All Events' : 'My Events' };
  }

  if (subcategory === 'InvitationApproved' || subcategory === 'InvitationDeclined') {
    return { tab: 'All Events' };
  }

  return undefined;
};

/**
 * Some backend content templates bake the sender's name in and some do not.
 * `EventApproval` does, via the same userFullNameBuilder call this file uses to
 * build the title, so rendering title followed by content printed the name
 * twice. The RSVP templates are written in the first person ("I'm going to X")
 * and genuinely need the title in front of them, so the prefix is removed only
 * where it is actually present.
 *
 * The name inside `content` is frozen when the notification is created, so a
 * later preferred-name change stops the prefix matching. That row then falls
 * back to the previous output rather than losing the start of its sentence.
 */
const stripLeadingSenderName = (content: string, name: string): string => {
  const prefix = `${name} `;

  return name && content.startsWith(prefix) ? content.slice(prefix.length) : content;
};

/**
 * A UserNotifications document from `POST /notification/list` to everything the
 * dropdown row needs.
 *
 * Pure on purpose: no DOM, no store, no timezone formatting. The component still
 * owns the avatar fallback (generateCustomAvatar touches `document`) and the
 * timestamp formatting, which needs the viewer's timezone at render time.
 *
 * Note the feed projection drops `sender.partnerAdmin` and `sender.region`, so
 * unlike the legacy payload there is no sender timezone to render against. The
 * viewer's own timezone is all that is available, which is why no timezone
 * field appears on the row.
 */
export const formatNotificationFeedItem = (
  doc: NotificationFeedDocument | undefined | null,
  { role }: FormatOptions = {}
): NotificationFeedRow | null => {
  if (!doc?._id || !doc?.content || !doc?.subcategory) {
    return null;
  }

  const importRow = doc.subcategory === IMPORT_SUBCATEGORY ? buildImportRow(doc.importData) : null;
  const route = importRow ? IMPORT_ROUTES[doc.importData?.targetUserRole ?? ''] : ROUTES[doc.subcategory];

  // Renderable means it has somewhere to go or something to say. An import
  // whose role is missing has no destination, and dropping it over that would
  // hide a finished import from the admin who started it.
  if (!route && !importRow) {
    return null;
  }

  const sender = doc.sender ?? {};
  const senderName = userFullNameBuilder.build(sender.preferredFname, sender.fname, sender.lname);

  return {
    id: String(doc._id),
    subcategory: doc.subcategory,
    // An import is the one notification whose sender is its receiver, so the
    // sender's name is the reader's own and says nothing. Its headline is the
    // stored sentence instead, which already names the file, and its body is
    // the counts line rather than a continuation of that sentence.
    title: importRow ? doc.content : senderName,
    body: importRow ? importRow.summary : stripLeadingSenderName(doc.content, senderName),
    avatarUrl: sender.profilePic,
    avatarFirstName: sender.preferredFname || sender.fname,
    avatarLastName: sender.preferredLname || sender.lname,
    createdAt: doc.createdAt,
    isRead: doc.isNotificationRead === true,
    route,
    routeState: resolveRouteState(doc.subcategory, role),
    event: doc.event ?? null,
    importReport: importRow,
  };
};

/** Maps a page of documents to rows, dropping any that cannot be rendered. */
export const formatNotificationFeed = (
  docs: NotificationFeedDocument[] | undefined | null,
  options?: FormatOptions
): NotificationFeedRow[] =>
  (Array.isArray(docs) ? docs : [])
    .map(doc => formatNotificationFeedItem(doc, options))
    .filter((row): row is NotificationFeedRow => row !== null);
